import { SPECIALTY_IDS } from 'config/specialtyConfig';

// Subscription Plans Configuration
// Replace the priceId values with your actual Stripe Price IDs

export const SUBSCRIPTION_PLANS = [
  {
    id: 'pmrPrep1Month49',
    priceId: 'pmrPrep1Month49',
    name: '1 Month Access',
    price: 99,
    months: 1,
    pricePerMonth: 99.00,
    color: 'info',
    description: 'Quick review before exam day',
    popular: false,
  },
  {
    id: 'pmrPrep3Months85',
    priceId: 'pmrPrep3Months85',
    name: '3 Months Access',
    price: 170,
    months: 3,
    pricePerMonth: 56.67,
    color: 'info',
    description: 'Short-term focused preparation',
    popular: false,
  },
  {
    id: 'pmrPrep6Months119',
    priceId: 'pmrPrep6Months119',
    name: '6 Months Access',
    price: 219,
    months: 6,
    pricePerMonth: 36.50,
    color: 'success',
    description: 'Great for focused exam preparation',
    popular: false,
  },
  {
    id: 'pmrPrep12Months149',
    priceId: 'pmrPrep12Months149',
    name: '12 Months Access',
    price: 239,
    months: 12,
    pricePerMonth: 19.92,
    color: 'warning',
    description: 'One year of comprehensive study',
    popular: true,
  },
  {
    id: 'pmrPrep18Months189',
    priceId: 'pmrPrep18Months189',
    name: '18 Months Access',
    price: 299,
    months: 18,
    pricePerMonth: 16.61,
    color: 'primary',
    description: 'Extended access for thorough preparation',
    popular: false,
  },
  {
    id: 'pmrPrep24Months229',
    priceId: 'pmrPrep24Months229',
    name: '24 Months Access',
    price: 329,
    months: 24,
    pricePerMonth: 13.71,
    color: 'default',
    description: 'Two years of unlimited access',
    popular: false,
  },
  {
    id: 'pmrPrep36Months279',
    priceId: 'pmrPrep36Months279',
    name: '36 Months Access',
    price: 399,
    months: 36,
    pricePerMonth: 11.08,
    color: 'danger',
    description: 'Best value - 3 years access',
    popular: false,
  },
  {
    id: 'pmrPrep48Months339',
    priceId: 'pmrPrep48Months339',
    name: '48 Months Access',
    price: 485,
    months: 48,
    pricePerMonth: 10.10,
    color: 'success',
    description: 'Perfect for incoming PGY1s starting ahead',
    popular: false,
  },
];

// Helper to get the price for a plan
export const getEffectivePrice = (plan) => plan.price;

// Helper to get the per-month price
export const getEffectivePricePerMonth = (plan) => plan.price / plan.months;

export const FREE_TRIAL_DAYS = 14;

// Add-on discount for cross-specialty purchases
export const ADDON_DISCOUNT_PERCENT = 15;

export const getAddonPrice = (plan) => {
  const base = getEffectivePrice(plan);
  return +(base * (1 - ADDON_DISCOUNT_PERCENT / 100)).toFixed(2);
};

// Bundle discount for multi-specialty purchases
export const BUNDLE_DISCOUNT_PERCENT = 20;
export const ALL_THREE_DISCOUNT_PERCENT = 25;

export const BUNDLES = [
  { id: 'bundle_pmr_pain', name: 'PM&R + Pain Medicine', specialties: ['pmr', 'pain'], discountPercent: BUNDLE_DISCOUNT_PERCENT, color: 'primary', popular: false },
  { id: 'bundle_pmr_sports', name: 'PM&R + Sports Medicine', specialties: ['pmr', 'sports'], discountPercent: BUNDLE_DISCOUNT_PERCENT, color: 'info', popular: false },
  { id: 'bundle_pain_sports', name: 'Pain + Sports Medicine', specialties: ['pain', 'sports'], discountPercent: BUNDLE_DISCOUNT_PERCENT, color: 'danger', popular: false },
  { id: 'bundle_all', name: 'All Three Specialties', specialties: ['pmr', 'pain', 'sports'], discountPercent: ALL_THREE_DISCOUNT_PERCENT, color: 'warning', popular: true },
];

export const getBundlePrice = (basePlan, bundle, basePrice = null) => {
  const unitPrice = basePrice != null ? basePrice : basePlan.price;
  if (!bundle || !bundle.specialties || bundle.specialties.length < 2) {
    return { totalPrice: unitPrice, savedAmount: 0, individualTotal: unitPrice, discountPercent: 0 };
  }
  const individualTotal = unitPrice * bundle.specialties.length;
  const discountedTotal = +(individualTotal * (1 - bundle.discountPercent / 100)).toFixed(2);
  return { totalPrice: discountedTotal, savedAmount: +(individualTotal - discountedTotal).toFixed(2), individualTotal, discountPercent: bundle.discountPercent };
};

export const getBundlePlanIds = (basePlan, bundle) => {
  const result = {};
  for (const specId of bundle.specialties) {
    const plans = getPlansForSpecialty(specId);
    result[specId] = plans.find(p => p.months === basePlan.months)?.id;
  }
  return result;
};

export const getBundleById = (bundleId) => BUNDLES.find(b => b.id === bundleId);
const LEGACY_TRIAL_DAYS = 30;
const TRIAL_CHANGE_CUTOFF = new Date('2026-04-06T00:00:00');

// Get the correct trial duration based on when the user signed up
const getTrialDays = (trialStartDate) => {
  const startDate = new Date(trialStartDate);
  return startDate < TRIAL_CHANGE_CUTOFF ? LEGACY_TRIAL_DAYS : FREE_TRIAL_DAYS;
};

// Helper to get plan by ID (searches across all specialties)
export const getPlanById = (planId) => {
  for (const specId of Object.keys(SUBSCRIPTION_PLANS_BY_SPECIALTY)) {
    const found = SUBSCRIPTION_PLANS_BY_SPECIALTY[specId].find(plan => plan.id === planId);
    if (found) return found;
  }
  return undefined;
};

// Helper to check if trial is valid
export const isTrialValid = (trialStartDate) => {
  if (!trialStartDate) return false;
  const startDate = new Date(trialStartDate);
  const now = new Date();
  const diffTime = now - startDate;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays <= getTrialDays(trialStartDate);
};

// Helper to get days remaining in trial
export const getTrialDaysRemaining = (trialStartDate) => {
  if (!trialStartDate) return 0;
  const startDate = new Date(trialStartDate);
  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + getTrialDays(trialStartDate));
  const now = new Date();
  const diffDays = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
};

// Helper to check if subscription is valid
export const isSubscriptionValid = (subscriptionEndDate) => {
  if (!subscriptionEndDate) return false;
  const endDate = new Date(subscriptionEndDate);
  const now = new Date();
  return now < endDate;
};

// Helper to get days remaining in subscription
export const getSubscriptionDaysRemaining = (subscriptionEndDate) => {
  if (!subscriptionEndDate) return 0;
  const endDate = new Date(subscriptionEndDate);
  const now = new Date();
  const diffTime = endDate - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
};

// Per-specialty subscription field mapping.
// PMR uses the original fields (backwards compatible).
// Pain and Sports use prefixed fields.
const SPECIALTY_FIELDS = {
  pmr: {
    subscriptionEndDate: 'subscriptionEndDate',
    subscriptionStartDate: 'subscriptionStartDate',
    trialStartDate: 'trialStartDate',
    plan: 'plan',
    planId: 'planId',
    subsId: 'subsId',
    paymentIntentId: 'paymentIntentId',
    autoChargeStatus: 'autoChargeStatus',
    selectedPlanId: 'selectedPlanId',
    autoChargeCancelledAt: 'autoChargeCancelledAt',
    autoChargeCancelledReason: 'autoChargeCancelledReason',
    autoChargeError: 'autoChargeError',
    trialWarningEmailSent: 'trialWarningEmailSent',
    ultimateAdvisingStudent: 'ultimateAdvisingStudent',
    lifetimeAccess: 'lifetimeAccess',
    year: 'year',
    pmrProgram: 'pmrProgram',
  },
  pain: {
    subscriptionEndDate: 'painSubscriptionEndDate',
    subscriptionStartDate: 'painSubscriptionStartDate',
    trialStartDate: 'painTrialStartDate',
    plan: 'painPlan',
    planId: 'painPlanId',
    subsId: 'painSubsId',
    paymentIntentId: 'painPaymentIntentId',
    autoChargeStatus: 'painAutoChargeStatus',
    selectedPlanId: 'painSelectedPlanId',
    autoChargeCancelledAt: 'painAutoChargeCancelledAt',
    autoChargeCancelledReason: 'painAutoChargeCancelledReason',
    autoChargeError: 'painAutoChargeError',
    trialWarningEmailSent: 'painTrialWarningEmailSent',
    ultimateAdvisingStudent: 'painUltimateAdvisingStudent',
    lifetimeAccess: 'painLifetimeAccess',
    year: 'painYear',
    pmrProgram: 'painPmrProgram',
  },
  sports: {
    subscriptionEndDate: 'sportsSubscriptionEndDate',
    subscriptionStartDate: 'sportsSubscriptionStartDate',
    trialStartDate: 'sportsTrialStartDate',
    plan: 'sportsPlan',
    planId: 'sportsPlanId',
    subsId: 'sportsSubsId',
    paymentIntentId: 'sportsPaymentIntentId',
    autoChargeStatus: 'sportsAutoChargeStatus',
    selectedPlanId: 'sportsSelectedPlanId',
    autoChargeCancelledAt: 'sportsAutoChargeCancelledAt',
    autoChargeCancelledReason: 'sportsAutoChargeCancelledReason',
    autoChargeError: 'sportsAutoChargeError',
    trialWarningEmailSent: 'sportsTrialWarningEmailSent',
    ultimateAdvisingStudent: 'sportsUltimateAdvisingStudent',
    lifetimeAccess: 'sportsLifetimeAccess',
    year: 'sportsYear',
    pmrProgram: 'sportsPmrProgram',
  },
};

// Per-specialty subscription plans (Pain and Sports use different Stripe price IDs)
export const SUBSCRIPTION_PLANS_BY_SPECIALTY = {
  pmr: SUBSCRIPTION_PLANS,
  pain: SUBSCRIPTION_PLANS.map(plan => ({
    ...plan,
    id: plan.id.replace('pmrPrep', 'painPrep'),
    priceId: plan.priceId.replace('pmrPrep', 'painPrep'),
    ...(plan.months === 48 ? { description: 'Best value for long-term preparation' } : {}),
  })),
  sports: SUBSCRIPTION_PLANS.map(plan => ({
    ...plan,
    id: plan.id.replace('pmrPrep', 'sportsPrep'),
    priceId: plan.priceId.replace('pmrPrep', 'sportsPrep'),
    ...(plan.months === 48 ? { description: 'Best value for long-term preparation' } : {}),
  })),
};

// Get subscription plans for a specific specialty
export const getPlansForSpecialty = (specialtyId = 'pmr') => {
  return SUBSCRIPTION_PLANS_BY_SPECIALTY[specialtyId] || SUBSCRIPTION_PLANS;
};

// Get the subscription/trial field names for a specialty
export const getSpecialtyFields = (specialtyId = 'pmr') => {
  return SPECIALTY_FIELDS[specialtyId] || SPECIALTY_FIELDS.pmr;
};

// Helper to check if user has PAID access (subscription OR UAS OR lifetime — NOT trial)
// When specialtyId is provided, checks the specialty-specific fields.
export const hasPaidAccess = (userDetails, specialtyId = 'pmr') => {
  if (!userDetails) return false;

  const fields = getSpecialtyFields(specialtyId);

  if (userDetails[fields.lifetimeAccess] === true || userDetails[fields.lifetimeAccess] === "true") {
    return true;
  }

  if (userDetails[fields.ultimateAdvisingStudent] === true || userDetails[fields.ultimateAdvisingStudent] === "true") {
    return true;
  }

  if (userDetails[fields.subscriptionEndDate] && isSubscriptionValid(userDetails[fields.subscriptionEndDate])) {
    return true;
  }

  return false;
};

// Helper to check if user has any active access (trial OR subscription OR UAS OR lifetime)
// When specialtyId is provided, checks the specialty-specific fields.
export const hasActiveAccess = (userDetails, specialtyId = 'pmr') => {
  if (!userDetails) return false;

  const fields = getSpecialtyFields(specialtyId);

  if (userDetails[fields.lifetimeAccess] === true || userDetails[fields.lifetimeAccess] === "true") {
    return true;
  }

  if (userDetails[fields.ultimateAdvisingStudent] === true || userDetails[fields.ultimateAdvisingStudent] === "true") {
    return true;
  }

  if (userDetails[fields.trialStartDate] && isTrialValid(userDetails[fields.trialStartDate])) {
    return true;
  }

  if (userDetails[fields.subscriptionEndDate] && isSubscriptionValid(userDetails[fields.subscriptionEndDate])) {
    return true;
  }

  return false;
};

// Check if user qualifies for addon discount on a target specialty
// (must have ever had a paid subscription to at least one OTHER specialty — includes expired)
export const qualifiesForAddon = (userDetails, targetSpecialtyId) => {
  if (!userDetails) return false;
  return SPECIALTY_IDS.some(id => {
    if (id === targetSpecialtyId) return false;
    const fields = getSpecialtyFields(id);
    if (userDetails[fields.lifetimeAccess] === true || userDetails[fields.lifetimeAccess] === 'true') return true;
    if (userDetails[fields.ultimateAdvisingStudent] === true || userDetails[fields.ultimateAdvisingStudent] === 'true') return true;
    // Any subscription (active or expired) qualifies
    if (userDetails[fields.subscriptionEndDate]) return true;
    return false;
  });
};
